import React, { useState } from "react";
import { Link } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import Modal from "../../components/common/Modal";
import { useGetRolesQuery, useDeleteRoleMutation } from "../../store/api";

const RoleList = () => {
  const [businessIdFilter, setBusinessIdFilter] = useState("");
  const { data, isLoading, isError, error } = useGetRolesQuery({
    businessId: businessIdFilter,
  });
  const [deleteRole, deleteState] = useDeleteRoleMutation();

  // Modal state
  const [modalState, setModalState] = useState({
    isOpen: false,
    roleId: null,
    roleName: null,
  });

  const roles = Array.isArray(data) ? data : [];

  const openModal = (roleId, roleName) => {
    setModalState({ isOpen: true, roleId, roleName });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, roleId: null, roleName: null });
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteRole(modalState.roleId).unwrap();
      closeModal();
    } catch (err) {
      // error handled by UI
    }
  };

  const handleDelete = (roleId, roleName) => {
    openModal(roleId, roleName);
  };

  const columns = [
    { key: "name", label: "Role Name" },
    { key: "description", label: "Description" },
    { key: "scope", label: "Scope" },
    { key: "actions", label: "Actions" },
  ];

  const rows = roles.map((role) => {
    const id = role.roleId || role.id;
    return {
      id,
      name: role.name || role.roleName,
      description: role.description || "-",
      scope: role.businessId ? `Business: ${role.businessId}` : "Global",
      actions: (
        <div className="flex gap-3">
          <Link
            to={`/roles/${id}/permissions`}
            className="text-sm font-semibold text-[#4f46e5]"
          >
            Permissions
          </Link>
          <Link
            to={`/roles/${id}/edit`}
            className="text-sm font-semibold text-[#4f46e5]"
          >
            Edit
          </Link>
          <button
            type="button"
            className="text-sm font-semibold text-rose-600"
            onClick={() => handleDelete(id, role.name || role.roleName)}
          >
            Delete
          </button>
        </div>
      ),
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Roles"
        subtitle="Manage user roles and permissions."
        actions={
          <Link to="/roles/new">
            <Button size="sm">Add Role</Button>
          </Link>
        }
      />

      <div className="flex gap-3">
        <div className="flex-1 max-w-md">
          <Input
            name="businessIdFilter"
            placeholder="Filter by Business ID (leave empty for all roles)"
            value={businessIdFilter}
            onChange={(e) => setBusinessIdFilter(e.target.value)}
          />
        </div>
      </div>

      <Card className="p-0">
        <div className="border-b border-slate-100 px-6 py-4 text-sm font-semibold text-slate-700">
          All Roles
        </div>
        <div className="px-6 py-4">
          {isLoading && (
            <p className="text-sm text-slate-500">Loading roles...</p>
          )}
          {isError && (
            <Alert tone="error">
              {error?.data?.detail || "Failed to load roles."}
            </Alert>
          )}
          {!isLoading && !isError && (
            <Table columns={columns} rows={rows} emptyText="No roles yet." />
          )}
        </div>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title="Delete Role"
        message={`Are you sure you want to delete the role "${modalState.roleName}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        confirmText="Delete Role"
        isLoading={deleteState.isLoading}
      />
    </div>
  );
};

export default RoleList;
